import { useEffect } from 'react';

import { EventEmitter, createEventEmitter } from 'lib/speechify/EventEmitter';

export enum OfflineModeStatus {
  OFFLINE = 'OFFLINE',
  ONLINE = 'ONLINE'
}

export let latestOfflineModeStatus: OfflineModeStatus = OfflineModeStatus.ONLINE;

export const offlineModeStatusEmitter: EventEmitter<{
  onOfflineModeStatusChange: {
    offlineModeStatus: OfflineModeStatus;
  };
}> = createEventEmitter({ events: ['onOfflineModeStatusChange'] });

offlineModeStatusEmitter.on('onOfflineModeStatusChange', ({ offlineModeStatus }) => {
  latestOfflineModeStatus = offlineModeStatus;
});

export const goOnline = () => {
  offlineModeStatusEmitter.emit('onOfflineModeStatusChange', { offlineModeStatus: OfflineModeStatus.ONLINE });
};

export const goOffline = () => {
  offlineModeStatusEmitter.emit('onOfflineModeStatusChange', { offlineModeStatus: OfflineModeStatus.OFFLINE });
};

export const useSyncWithWindowOnlineOfflineStatus = () => {
  useEffect(() => {
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);
};

/**
 * Executes an asynchronous callback function while temporarily forcing the application's
 * offline status state to OFFLINE, only if it was initially ONLINE.
 * After the callback completes (successfully or with an error), it checks the browser's
 * actual online status (`navigator.onLine`). If the browser is online, it ensures the
 * application state is set back to ONLINE.
 *
 * This is useful for operations that should attempt to use cached data first by simulating
 * an offline state, without leaving the application state incorrectly set to ONLINE if
 * the browser truly went offline during the operation.
 *
 * @param cb The asynchronous callback function to execute.
 * @returns A promise that resolves with the return value of the callback function.
 */
export const runWithTemporaryOfflineState = async <T>(cb: () => Promise<T>): Promise<T> => {
  const wasInitiallyOnline = latestOfflineModeStatus === OfflineModeStatus.ONLINE;

  // Force offline only if we were initially online
  if (wasInitiallyOnline) {
    goOffline();
  }

  try {
    // Execute the callback
    const result = await cb();
    return result;
  } finally {
    // After completion (success or error), check the actual browser status
    if (navigator.onLine) {
      // If the browser is online, ensure the app state is set back to online.
      // This corrects the state if we temporarily forced it offline OR
      // if the app was already offline but the browser came back online.
      goOnline();
    }
    // If navigator.onLine is false, we do nothing here. The app state will remain
    // OFFLINE (either because we forced it, or due to the 'offline' event listener).
  }
};
